/**
 * WebABTestDialog Component
 *
 * Dialog for creating and editing Web A/B test placements.
 * A/B test variants reference existing published WebPlacements.
 *
 * Features:
 * - Name, alias, and description fields
 * - Variant management with weight distribution
 * - Placement picker for variant selection
 * - Validation before save
 *
 * @example
 * ```tsx
 * <WebABTestDialog
 *   open={open}
 *   onClose={onClose}
 *   onSave={handleSave}
 *   projectId={projectId}
 *   mode="create"
 * />
 * ```
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  TextField,
  Typography,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScienceIcon from '@mui/icons-material/Science';
import { SDialog } from '@modules/ui-components';
import type {
  WebPlacement,
  WebABVariant,
  CreateWebPlacementInput,
  UpdateWebPlacementInput,
} from '@modules/webplacements/types/webplacements.types';
import {
  WEB_PLACEMENT_VALIDATION,
  WEB_AB_VALIDATION,
  getDefaultWebABVariantRole,
  isValidWebAlias,
  isReservedWebAlias,
} from '@modules/webplacements/types/webplacements.types';
import { WebPlacementService } from '@modules/firebase-core/services/WebPlacementService';
import { WebABVariantEditor } from './WebABVariantEditor';
import { WebPlacementPickerDialog } from './WebPlacementPickerDialog';
import { distributeWeightsEvenly } from '@modules/placements/utils/weightDistribution';

export interface WebABTestDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog is closed */
  onClose: () => void;
  /** Callback when A/B test is saved */
  onSave: (data: CreateWebPlacementInput | UpdateWebPlacementInput) => Promise<void>;
  /** Project ID for validation and loading placements */
  projectId: string;
  /** Mode: create or edit */
  mode: 'create' | 'edit';
  /** Existing A/B test for edit mode */
  existingABTest?: WebPlacement;
}

interface FormState {
  name: string;
  alias: string;
  description: string;
  tags: string;
  variants: WebABVariant[];
}

interface FormErrors {
  name?: string;
  alias?: string;
  variants?: string;
}

/**
 * Create default variants (A and B with 50/50 split)
 */
const createDefaultVariants = (): WebABVariant[] => {
  const weights = distributeWeightsEvenly(2);
  return [
    {
      id: 'A',
      name: 'Variant A',
      role: getDefaultWebABVariantRole(0),
      weight: weights[0],
      webPlacementId: '',
      webPlacementAlias: '',
      webPlacementName: '',
    },
    {
      id: 'B',
      name: 'Variant B',
      role: getDefaultWebABVariantRole(1),
      weight: weights[1],
      webPlacementId: '',
      webPlacementAlias: '',
      webPlacementName: '',
    },
  ];
};

/**
 * WebABTestDialog Component
 */
export const WebABTestDialog: React.FC<WebABTestDialogProps> = ({
  open,
  onClose,
  onSave,
  projectId,
  mode,
  existingABTest,
}) => {
  // Form state
  const [formState, setFormState] = useState<FormState>({
    name: '',
    alias: '',
    description: '',
    tags: '',
    variants: createDefaultVariants(),
  });

  // UI state
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [aliasCheckLoading, setAliasCheckLoading] = useState(false);

  // Published placements for variant selection
  const [publishedPlacements, setPublishedPlacements] = useState<WebPlacement[]>([]);
  const [placementsLoading, setPlacementsLoading] = useState(false);

  // Placement picker state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerVariantId, setPickerVariantId] = useState<string | null>(null);

  // Service instance
  const service = useMemo(() => new WebPlacementService(), []);

  // Load published placements when dialog opens
  useEffect(() => {
    if (!open || !projectId) return;

    const loadPlacements = async () => {
      setPlacementsLoading(true);
      try {
        const placements = await service.listPublishedSingle(projectId);
        setPublishedPlacements(placements);
      } catch (error) {
        console.error('Failed to load published placements:', error);
      } finally {
        setPlacementsLoading(false);
      }
    };

    loadPlacements();
  }, [open, projectId, service]);

  // Initialize form state when dialog opens or existingABTest changes
  useEffect(() => {
    if (!open) return;

    if (mode === 'edit' && existingABTest) {
      setFormState({
        name: existingABTest.name,
        alias: existingABTest.alias,
        description: existingABTest.description || '',
        tags: existingABTest.tags?.join(', ') || '',
        variants: existingABTest.variants || createDefaultVariants(),
      });
    } else {
      // Create mode - reset form
      setFormState({
        name: '',
        alias: '',
        description: '',
        tags: '',
        variants: createDefaultVariants(),
      });
    }

    setErrors({});
    setIsSaving(false);
  }, [open, mode, existingABTest]);

  // Auto-focus name field
  useEffect(() => {
    if (open && mode === 'create') {
      const timer = setTimeout(() => {
        const nameField = document.querySelector<HTMLInputElement>('[name="ab-test-name"]');
        nameField?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, mode]);

  // Field change handlers
  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setFormState((prev) => ({ ...prev, name }));
    if (errors.name) {
      setErrors((prev) => ({ ...prev, name: undefined }));
    }
  }, [errors.name]);

  const handleAliasChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const alias = e.target.value.replace(/[^a-zA-Z0-9_-]/g, '');
    setFormState((prev) => ({ ...prev, alias }));
    if (errors.alias) {
      setErrors((prev) => ({ ...prev, alias: undefined }));
    }
  }, [errors.alias]);

  const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFormState((prev) => ({ ...prev, description: e.target.value }));
  }, []);

  const handleTagsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFormState((prev) => ({ ...prev, tags: e.target.value }));
  }, []);

  const handleVariantsChange = useCallback((variants: WebABVariant[]) => {
    setFormState((prev) => ({ ...prev, variants }));
    if (errors.variants) {
      setErrors((prev) => ({ ...prev, variants: undefined }));
    }
  }, [errors.variants]);

  // Open placement picker for a variant
  const handleOpenPicker = useCallback((variantId: string) => {
    setPickerVariantId(variantId);
    setPickerOpen(true);
  }, []);

  // Handle placement selection from picker
  const handlePlacementSelect = useCallback((placement: WebPlacement) => {
    if (!pickerVariantId) return;

    setFormState((prev) => ({
      ...prev,
      variants: prev.variants.map((v) =>
        v.id === pickerVariantId
          ? {
              ...v,
              webPlacementId: placement.id,
              webPlacementAlias: placement.alias,
              webPlacementName: placement.name,
            }
          : v
      ),
    }));

    setPickerOpen(false);
    setPickerVariantId(null);

    if (errors.variants) {
      setErrors((prev) => ({ ...prev, variants: undefined }));
    }
  }, [pickerVariantId, errors.variants]);

  // Validation
  const validate = async (): Promise<boolean> => {
    const newErrors: FormErrors = {};

    // Validate name
    const trimmedName = formState.name.trim();
    if (!trimmedName) {
      newErrors.name = 'Name is required';
    } else if (trimmedName.length > WEB_PLACEMENT_VALIDATION.NAME_MAX_LENGTH) {
      newErrors.name = `Name must be at most ${WEB_PLACEMENT_VALIDATION.NAME_MAX_LENGTH} characters`;
    }

    // Validate alias (only for create mode)
    if (mode === 'create') {
      const alias = formState.alias.trim();
      if (!alias) {
        newErrors.alias = 'Alias is required';
      } else if (!isValidWebAlias(alias)) {
        newErrors.alias = `Invalid alias. Must be ${WEB_PLACEMENT_VALIDATION.ALIAS_MIN_LENGTH}-${WEB_PLACEMENT_VALIDATION.ALIAS_MAX_LENGTH} chars, letters, numbers, hyphens, underscores only`;
      } else if (isReservedWebAlias(alias)) {
        newErrors.alias = `"${alias}" is reserved. Please choose a different alias.`;
      } else {
        // Check uniqueness
        setAliasCheckLoading(true);
        try {
          const isUnique = await service.isAliasUnique(projectId, alias);
          if (!isUnique) {
            newErrors.alias = `Alias "${alias}" is already in use`;
          }
        } catch {
          newErrors.alias = 'Failed to check alias availability';
        } finally {
          setAliasCheckLoading(false);
        }
      }
    }

    // Validate variants
    if (formState.variants.length < WEB_AB_VALIDATION.VARIANT_MIN_COUNT) {
      newErrors.variants = `At least ${WEB_AB_VALIDATION.VARIANT_MIN_COUNT} variants are required`;
    } else {
      // Check all variants have placement
      const missingPlacements = formState.variants.filter((v) => !v.webPlacementId);
      if (missingPlacements.length > 0) {
        newErrors.variants = `Select placement for: ${missingPlacements.map((v) => v.name).join(', ')}`;
      }

      // Check weights sum to 100
      const weightSum = formState.variants.reduce((sum, v) => sum + v.weight, 0);
      if (weightSum !== WEB_AB_VALIDATION.WEIGHT_SUM) {
        newErrors.variants = `Variant weights must sum to ${WEB_AB_VALIDATION.WEIGHT_SUM} (currently: ${weightSum})`;
      }

      // Check at least one variant has weight > 0
      const hasActiveVariant = formState.variants.some((v) => v.weight > 0);
      if (!hasActiveVariant) {
        newErrors.variants = 'At least one variant must have weight > 0';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    const isValid = await validate();
    if (!isValid) return;

    const tags = formState.tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);

    setIsSaving(true);
    try {
      if (mode === 'create') {
        const data: CreateWebPlacementInput = {
          name: formState.name.trim(),
          alias: formState.alias.trim(),
          // Only include description if it has a value (Firestore rejects undefined)
          ...(formState.description.trim() && { description: formState.description.trim() }),
          ...(tags.length && { tags }),
          type: 'ab_test',
          variants: formState.variants,
          createdBy: '', // Will be set by the parent component
        };
        await onSave(data);
      } else {
        const data: UpdateWebPlacementInput = {
          name: formState.name.trim(),
          // Only include description if it has a value (Firestore rejects undefined)
          ...(formState.description.trim() && { description: formState.description.trim() }),
          tags,
          variants: formState.variants,
        };
        await onSave(data);
      }
      onClose();
    } catch (error) {
      console.error('Failed to save A/B test:', error);
      setErrors((prev) => ({
        ...prev,
        name: error instanceof Error ? error.message : 'Failed to save',
      }));
    } finally {
      setIsSaving(false);
    }
  };

  // Currently selected variant IDs (for excluding from picker)
  const selectedPlacementIds = useMemo(() => {
    return formState.variants
      .filter((v) => v.webPlacementId && v.id !== pickerVariantId)
      .map((v) => v.webPlacementId);
  }, [formState.variants, pickerVariantId]);

  const isLoading = isSaving || aliasCheckLoading;

  return (
    <>
      <SDialog
        open={open}
        onClose={onClose}
        title={mode === 'create' ? 'Create A/B Test' : 'Edit A/B Test'}
        maxWidth="md"
        fullWidth
        primaryAction={{
          label: mode === 'create' ? 'Create' : 'Save',
          onClick: handleSave,
          loading: isLoading,
          disabled: isLoading,
        }}
      >
        {/* A/B Test Icon Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <ScienceIcon color="primary" />
          <Typography variant="subtitle1" color="primary">
            Web A/B Test Configuration
          </Typography>
        </Box>

        {/* Basic Info */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          <TextField
            name="ab-test-name"
            data-testid="webabt-dialog-name"
            label="A/B Test Name"
            value={formState.name}
            onChange={handleNameChange}
            error={!!errors.name}
            helperText={errors.name}
            required
            fullWidth
            disabled={isSaving}
          />

          <TextField
            data-testid="webabt-dialog-alias"
            label="URL Alias"
            value={formState.alias}
            onChange={handleAliasChange}
            error={!!errors.alias}
            helperText={errors.alias || `URL: /${formState.alias || 'your-alias'}`}
            required
            fullWidth
            disabled={mode === 'edit' || isSaving}
            InputProps={{
              startAdornment: <Typography color="text.secondary">/</Typography>,
              endAdornment: aliasCheckLoading ? <CircularProgress size={20} /> : null,
            }}
          />

          <TextField
            data-testid="webabt-dialog-description"
            label="Description"
            value={formState.description}
            onChange={handleDescriptionChange}
            multiline
            rows={2}
            fullWidth
            disabled={isSaving}
          />

          <TextField
            data-testid="webabt-dialog-tags"
            label="Tags"
            value={formState.tags}
            onChange={handleTagsChange}
            helperText="Comma-separated tags for reporting lookup"
            fullWidth
            disabled={isSaving}
          />
        </Box>

        {/* Variants Section */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography>Variants</Typography>
              <Chip
                label={formState.variants.length}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {errors.variants && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.variants}
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select published web placements for each variant. Adjust weights to control traffic distribution.
            </Typography>

            <WebABVariantEditor
              variants={formState.variants}
              onChange={handleVariantsChange}
              availablePlacements={publishedPlacements}
              placementsLoading={placementsLoading}
              error={errors.variants}
              disabled={isSaving}
              onOpenPicker={handleOpenPicker}
            />
          </AccordionDetails>
        </Accordion>

        {/* Edit Mode Info */}
        {mode === 'edit' && existingABTest && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              ID: {existingABTest.id}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Status: {existingABTest.status}
            </Typography>
            {existingABTest.abSalt && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Salt: {existingABTest.abSalt.substring(0, 8)}...
              </Typography>
            )}
          </Box>
        )}
      </SDialog>

      {/* Placement Picker Dialog */}
      <WebPlacementPickerDialog
        open={pickerOpen}
        onClose={() => {
          setPickerOpen(false);
          setPickerVariantId(null);
        }}
        onSelect={handlePlacementSelect}
        placements={publishedPlacements}
        loading={placementsLoading}
        selectedId={pickerVariantId ? formState.variants.find((v) => v.id === pickerVariantId)?.webPlacementId : undefined}
        excludeIds={selectedPlacementIds}
        title="Select Web Placement for Variant"
      />
    </>
  );
};

export default WebABTestDialog;
